import React, { useEffect } from "react";

interface Font {
  id: string;
  fileName: string | null;
  fontName: string;
  fontFamily: string;
  src: string | null;
  isPreloaded: boolean;
}

interface FontLoaderProps {
  fonts: Font[];
}

const FontLoader: React.FC<FontLoaderProps> = ({ fonts }) => {
  useEffect(() => {
    const loadFont = (font: Font) => {
      return new Promise<void>((resolve, reject) => {
        if (!font.src) return resolve();

        // helper to try one way
        const tryLoad = (family: string) =>
          new FontFace(family, `url(${font.src})`).load();

        // 1) Try unquoted first (works in Chrome desktop)
        tryLoad(font.fontFamily)
          .then((loadedFont) => {
            document.fonts.add(loadedFont);
            resolve();
          })
          .catch(() => {
            // 2) Fallback: retry with quotes (needed on Firefox/iOS Safari when spaces exist)
            if (/\s/.test(font.fontFamily)) {
              tryLoad(`"${font.fontFamily}"`)
                .then((loadedFont) => {
                  document.fonts.add(loadedFont);
                  resolve();
                })
                .catch((error) => {
                  console.error(
                    "FontLoader: Font loading error post quoting:",
                    font.fontName,
                    error
                  );
                  reject(error);
                });
            } else {
              reject(new Error(`FontLoader: Failed to load ${font.fontName}`));
            }
          });
      });
    };
    const loadFonts = async () => {
      try {
        await Promise.all(fonts.map(loadFont));
        console.log("FontLoader: All fonts loaded");
        // @ts-ignore
        window.customFontsLoaded = true;
        // fire an event to notify that fonts are loaded as well.
        window.dispatchEvent(new Event("customFontsLoaded"));
      } catch (error) {
        console.error("FontLoader: Error loading fonts", error);
      }
    };

    loadFonts();
  }, [fonts]);

  return null; // FontLoader doesn't render anything
};

export default FontLoader;
